import { memo, useEffect, useRef, useCallback, useState } from 'react'
import { Heart, MessageCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useGallery } from '../context/GalleryContext'
import { useProgressiveImage, getTargetResolution } from '../hooks/useProgressiveImage'
import type { GalleryImage as GalleryImageType, GridPosition } from '../types'
import SubscriptionGate from './SubscriptionGate'
import GalleryPurchaseModal from './GalleryPurchaseModal'

interface GalleryImageProps {
  image: GalleryImageType
  position: GridPosition
  isVisible: boolean
  currentScale: number
  onClick?: () => void
}

// Maximum distance (px) mouse can move between down and up to still count as a click
const CLICK_THRESHOLD = 5

function GalleryImage({
  image,
  position,
  isVisible,
  currentScale,
  onClick
}: GalleryImageProps) {
  const {
    refreshImageUrl,
    likedImageIds,
    toggleLike,
    markImageAsPurchased,
    purchasedImageIds
  } = useGallery()
  const navigate = useNavigate()
  const isLiked = likedImageIds.has(image.id)
  const isPurchased = purchasedImageIds.has(image.id)
  const targetResolution = getTargetResolution(currentScale)

  // Purchase modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  // Handle purchase button click - opens modal
  const handlePurchaseClick = useCallback(() => {
    setShowPurchaseModal(true)
  }, [])

  // Handle successful purchase
  const handlePurchaseSuccess = useCallback(() => {
    markImageAsPurchased(image.id)
  }, [markImageAsPurchased, image.id])

  // Track mouse/touch position for click vs drag detection
  const pointerStart = useRef<{ x: number; y: number } | null>(null)

  // Handle pointer down - record start position
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY }
  }, [])

  // Handle pointer up - only trigger click if didn't drag
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!pointerStart.current || !onClick) return

    const dx = Math.abs(e.clientX - pointerStart.current.x)
    const dy = Math.abs(e.clientY - pointerStart.current.y)

    // Only trigger click if mouse barely moved (not a drag)
    if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) {
      onClick()
    }

    pointerStart.current = null
  }, [onClick])

  const {
    displayedSrc,
    loadingSrc,
    loadState,
    isTransitioning,
    handleError,
    onTransitionEnd
  } = useProgressiveImage(
    image,
    isVisible,
    targetResolution,
    refreshImageUrl
  )

  // Auto-complete transition after a short delay (backup for onTransitionEnd)
  useEffect(() => {
    if (isTransitioning && loadingSrc) {
      const timer = setTimeout(onTransitionEnd, 350)
      return () => clearTimeout(timer)
    }
  }, [isTransitioning, loadingSrc, onTransitionEnd])

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        backgroundColor: image.dominant_color || '#1f2937',
        willChange: isVisible ? 'transform' : 'auto',
        contain: 'layout style paint',
      }}
      className="rounded-lg overflow-hidden shadow-lg cursor-pointer"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {/* Skeleton shimmer while no image */}
      {!displayedSrc && !loadingSrc && (
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 -translate-x-full animate-shimmer"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            }}
          />
        </div>
      )}

      {/* Base layer: Currently displayed image (always visible) */}
      {displayedSrc && (
        <img
          src={displayedSrc}
          alt={image.title || ''}
          onError={handleError}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            imageRendering: 'auto' as const,
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden'
          }}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      )}

      {/* Top layer: New image fading in (stacked on top, no gap possible) */}
      {loadingSrc && isTransitioning && (
        <img
          src={loadingSrc}
          alt={image.title || ''}
          onTransitionEnd={onTransitionEnd}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          style={{
            opacity: 1,
            imageRendering: 'auto' as const,
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden'
          }}
          loading="eager"
          decoding="async"
          draggable={false}
        />
      )}

      {/* Loading indicator for higher resolution */}
      {loadState === 'thumbnail' && image.hasAccess && !isTransitioning && (
        <div className="absolute top-2 right-2 z-10">
          <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* Subscription gate overlay for restricted content */}
      {!image.hasAccess && image.access_level !== 'public' && (
        <SubscriptionGate
          accessLevel={image.access_level}
          price={image.price}
          onPurchase={handlePurchaseClick}
        />
      )}

      {/* Unlocked badge for purchased images */}
      {isPurchased && image.access_level === 'premium' && (
        <div className="absolute top-2 left-2 z-10 bg-[#4AA3CC] text-white text-xs font-bold px-2 py-1 rounded-full">
          UNLOCKED
        </div>
      )}

      {/* Purchase modal */}
      {showPurchaseModal && (
        <GalleryPurchaseModal
          image={image}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      {/* Heart button */}
      <button
        onPointerDown={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        onPointerUp={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          toggleLike(image.id)
        }}
        className="absolute bottom-2 right-2 z-10 w-8 h-8 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all active:scale-95"
        aria-label={isLiked ? "Unlike" : "Like"}
        style={{ touchAction: 'manipulation' }}
      >
        <Heart
          size={16}
          className={isLiked ? 'text-red-500 fill-red-500' : 'text-white/80 hover:text-white'}
        />
      </button>

      {/* Message button */}
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          navigate(`/messages?replyToGalleryImage=${image.id}`)
        }}
        className="absolute bottom-2 left-2 z-10 w-8 h-8 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all active:scale-95"
        aria-label="Send message about this image"
      >
        <MessageCircle size={16} className="text-white/80 hover:text-white" />
      </button>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  )
}

// Export memoized version for performance
export default memo(GalleryImage, (prevProps, nextProps) => {
  // Only re-render if these props changed
  return (
    prevProps.image.id === nextProps.image.id &&
    prevProps.image.hasAccess === nextProps.image.hasAccess &&
    prevProps.image.price === nextProps.image.price &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.position.x === nextProps.position.x &&
    prevProps.position.y === nextProps.position.y &&
    prevProps.position.width === nextProps.position.width &&
    prevProps.position.height === nextProps.position.height &&
    // Only care about scale when it crosses resolution thresholds
    getTargetResolution(prevProps.currentScale) === getTargetResolution(nextProps.currentScale)
  )
})
